import { toast } from 'react-toastify';

export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

export const showToaster = (type, message, options = {}) => {
  const defaultOptions = {
    position: 'top-center',
    autoClose: 12000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'light',
    ...options, // Merge any additional options passed to the function
  };

  // Display the toast based on the type
  if (type === 'success') {
    toast.success(message, defaultOptions);
  } else if (type === 'error') {
    toast.error(message, defaultOptions);
  } else {
    console.warn('showToast called with invalid type. Expected "success" or "error".');
  }
};

export const maskUserFullName = (user, maskChar = '*') => {
  if (!user?.id) return null;

  const { firstName, lastName } = user?.attributes?.profile || {};

  if (!firstName || !lastName) return null;

  // Mask first name (only show first character)
  const maskedFirstName = firstName[0] + maskChar.repeat(firstName.length - 1);

  // Mask last name (only show first character)
  const maskedLastName = lastName[0] + maskChar.repeat(lastName.length - 1);

  return `${maskedFirstName} ${maskedLastName}`;
};


export const getListingBids = listing => {
  if (!listing?.id) return null;
  return listing?.attributes?.metadata?.userBids || [];
};

export const getOfferParams = transaction => {
  // Must have a valid UUID to consider this a real transaction
  if (!transaction?.id?.uuid) {
    return null;
  }

  // Drill into protectedData and return offerParams if it exists
  return transaction.attributes?.protectedData?.offerParams ?? null;
};


export const formatMaybeMoney = (intl, money) => {
  if (!money) return '';
  try {
    return formatMoney(intl, money);
  } catch (e) {
    return formatPublicMoney(money, intl);
  }
};

export const getTxPricingDetails = (transaction, intl, marketplaceCurrency) => {
  const pd = transaction?.attributes?.protectedData || {};
  const isBidding = !!pd?.isBidding;
  const isOffer = !!pd?.isOffer;

  // ✅ BID flow (you showed: biddingAmount, biddingCurrency, biddingTime)
  if (isBidding) {
    const bidAmount = pd?.biddingAmount;
    const bidCurrency = pd?.biddingCurrency || marketplaceCurrency;

    const bidMoney =
      bidAmount != null && bidCurrency
        ? new sdkTypes.Money(bidAmount, bidCurrency)
        : null;

    const bidTime = pd?.biddingTime ? formatUnixToDate(pd.biddingTime, intl) : '';

    return {
      label: 'Bid details',
      value: [
        bidMoney ? `Bid: ${formatMaybeMoney(intl, bidMoney)}` : null,
        bidTime ? `Time: ${bidTime}` : null,
      ]
        .filter(Boolean)
        .join(' • '),
    };
  }

  // ✅ OFFER flow (you showed: protectedData.offerParams.price + currency)
  if (isOffer) {
    const offer = pd?.offerParams || {};
    const offerMoney =
      offer?.price != null && offer?.currency
        ? new sdkTypes.Money(offer.price, offer.currency)
        : null;

    const offerType = offer?.type ? titleCaseFromSlug(offer.type) : '';
    const offerCreated = offer?.createdAt ? formatUnixToDate(offer.createdAt, intl) : '';

    return {
      label: 'Offer details',
      value: [
        offerMoney ? `Offer: ${formatMaybeMoney(intl, offerMoney)}` : null,
        offerType ? `Type: ${offerType}` : null,
        offerCreated ? `Created: ${offerCreated}` : null,
      ]
        .filter(Boolean)
        .join(' • '),
    };
  }

  // ✅ DIRECT / fallback (use payinTotal if present)
  const total = transaction?.attributes?.payinTotal || transaction?.attributes?.payoutTotal;
  return {
    label: 'Price',
    value: total ? formatMaybeMoney(intl, total) : '-',
  };
};


export const clamp0 = n => (typeof n === 'number' && n > 0 ? n : 0);

export const getCountdownParts = targetMs => {
  const diff = clamp0(targetMs - Date.now());
  const totalSec = Math.floor(diff / 1000);
  const days = Math.floor(totalSec / 86400);
  const hrs = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  return { days, hrs, mins, secs, isEnded: diff === 0 };
};

export const pad2 = n => String(n).padStart(2, '0');

export const formatUnixToDateTime = (unixSeconds, intl) => {
  if (!unixSeconds) return '';
  const d = new Date(unixSeconds * 1000);
  if (Number.isNaN(d.getTime())) return '';
  return intl.formatDate(d, { year: 'numeric', month: 'short', day: '2-digit' });
};


export const normalizeMoney = (value, fallbackCurrency) => {
  if (!value) return null;
  if (typeof value?.amount === 'number' && value?.currency) {
    return { amount: value.amount, currency: value.currency };
  }
  if (typeof value === 'number' && fallbackCurrency) {
    return { amount: value, currency: fallbackCurrency };
  }
  if (typeof value?.amount === 'string' && value?.currency) {
    const parsed = Number(value.amount);
    if (Number.isFinite(parsed)) {
      return { amount: parsed, currency: value.currency };
    }
  }
  return null;
};


export const customFormatMoney = (money, intl) => {
  if (!money?.currency || typeof money?.amount !== 'number') return '--';
  return intl.formatNumber(money.amount, {
    style: 'currency',
    currency: money.currency,
    maximumFractionDigits: 0,
  });
};